import type { ReactNode } from 'react';
import { formatAccountingCurrency } from '@/utils/formatters';
import type { ColumnConfigEntry } from '@erp/shared-types';

export type FieldFormat =
  'string' | 'currency' | 'currency4' | 'date' | 'number' | 'integer' | 'float';

/** Lookup map type for field schema entries */
export type FieldSchemaMap = Map<string, { format: FieldFormat }>;

const NEGATIVE_HIGHLIGHT_FIELDS = new Set(['claim_amount', 'quantity']);

/** Format a raw value based on the field's format type */
function formatValue(value: unknown, format: FieldFormat): string {
  if (value == null) return '';

  switch (format) {
    case 'currency': {
      const num = Number(value);
      if (isNaN(num)) return 'N/A';
      return formatAccountingCurrency(num, 2);
    }
    case 'currency4': {
      const num = Number(value);
      if (isNaN(num)) return 'N/A';
      return formatAccountingCurrency(num, 4);
    }
    case 'number':
    case 'integer':
    case 'float': {
      const num = Number(value);
      if (isNaN(num)) return 'N/A';
      return String(num);
    }
    case 'date': {
      const str = String(value);
      // E844SD dates are stored as YYYYMMDD integers (e.g., 20250315)
      // Use local date parts to avoid UTC timezone shift
      if (/^\d{8}$/.test(str)) {
        const date = new Date(
          Number(str.substring(0, 4)),
          Number(str.substring(4, 6)) - 1,
          Number(str.substring(6, 8)),
        );
        if (!isNaN(date.getTime())) return date.toLocaleDateString('en-CA');
      }
      // ISO date strings (YYYY-MM-DD) also need local parsing
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const date = new Date(
          Number(str.substring(0, 4)),
          Number(str.substring(5, 7)) - 1,
          Number(str.substring(8, 10)),
        );
        if (!isNaN(date.getTime())) return date.toLocaleDateString('en-CA');
      }
      const date = new Date(str);
      if (isNaN(date.getTime())) return str;
      return date.toLocaleDateString('en-CA');
    }
    case 'string':
    default:
      return String(value);
  }
}

export type CellLabelMaps = Record<string, Record<string, string> | undefined>;

/**
 * Renders a cell value using the column's apiField and the field schema map
 * for formatting. All column config (field, format, alignment, etc.) comes
 * from the DB — no hardcoded column definitions.
 */
export function renderDynamicCell<T extends object>({
  transaction,
  config,
  fieldSchemaMap,
  labelMaps,
}: {
  transaction: T;
  config: ColumnConfigEntry;
  fieldSchemaMap?: FieldSchemaMap;
  labelMaps?: CellLabelMaps;
}): ReactNode {
  if (!config.apiField) return 'N/A';

  const raw =
    config.apiField in transaction
      ? (transaction as Record<string, unknown>)[config.apiField]
      : undefined;

  const labelMap = labelMaps?.[config.apiField];
  if (labelMap && raw != null) {
    const label = labelMap[String(raw)];
    if (label) return label;
  }

  const entry = fieldSchemaMap?.get(config.apiField);
  const format = (config.format as FieldFormat) ?? entry?.format ?? 'string';

  const formatted = formatValue(raw, format);

  if (
    config.apiField &&
    NEGATIVE_HIGHLIGHT_FIELDS.has(config.apiField) &&
    raw != null
  ) {
    const num = Number(raw);
    if (!isNaN(num) && num < 0) {
      return <span className="text-red-600">{formatted}</span>;
    }
  }

  return formatted;
}
